import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, LayoutTemplate, Trash2, Sparkles } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { todayStr } from "@/lib/taskConstants";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

const TemplateCard = ({ tpl, onUse, onDelete }) => (
  <div className="rounded-xl border p-4 space-y-2 hover:border-primary/50 transition-colors" data-testid={`template-card-${tpl.id}`}>
    <div className="flex items-start gap-3">
      <div className="h-9 w-9 rounded-lg flex items-center justify-center text-lg shrink-0"
        style={{ backgroundColor: `${tpl.color}1f`, border: `1px solid ${tpl.color}40` }}>
        {tpl.icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-heading font-semibold text-sm truncate">{tpl.name}</p>
        <p className="text-xs text-muted-foreground">
          {tpl.kind === "module" ? "Flujo de trabajo completo" : `${tpl.task_count} ${tpl.task_count === 1 ? "tarea" : "tareas"}`}
          {tpl.created_by_name ? ` · de ${tpl.created_by_name}` : ""}
        </p>
      </div>
      {tpl.builtin && <Badge variant="secondary" className="text-[10px] shrink-0"><Sparkles className="h-3 w-3 mr-1" /> nuxxum</Badge>}
    </div>
    {tpl.description && <p className="text-xs text-muted-foreground line-clamp-2">{tpl.description}</p>}
    <div className="flex gap-2 pt-1">
      <Button size="sm" className="h-8 text-xs flex-1" onClick={() => onUse(tpl)} data-testid={`template-use-btn-${tpl.id}`}>
        {tpl.kind === "module" ? (tpl.enabled ? "Abrir" : "Activar en este Espacio") : "Usar plantilla"}
      </Button>
      {!tpl.builtin && (
        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-destructive" onClick={() => onDelete(tpl)}
          data-testid={`template-delete-btn-${tpl.id}`} title="Eliminar plantilla">
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  </div>
);

export const TemplatesGalleryDialog = ({ workspaceId, open, onOpenChange }) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState(todayStr());
  const [creating, setCreating] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["templates", workspaceId],
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}/templates`)).data,
    enabled: open,
  });

  const pick = async (tpl) => {
    if (tpl.kind === "module") {
      try {
        const { data } = await api.post(`/workspaces/${workspaceId}/templates/${tpl.id}/use`, {});
        qc.invalidateQueries({ queryKey: ["workspace", workspaceId] });
        toast.success(`Módulo "${tpl.name}" activado en este Espacio`);
        onOpenChange(false);
        navigate(data.redirect);
      } catch (e) {
        toast.error(apiError(e));
      }
      return;
    }
    setSelected(tpl);
    setName(tpl.name);
    setStartDate(todayStr());
  };

  const create = async () => {
    if (!name.trim()) return toast.error("El nombre es obligatorio");
    setCreating(true);
    try {
      const { data: project } = await api.post(`/workspaces/${workspaceId}/templates/${selected.id}/use`,
        { name, start_date: startDate || null });
      qc.invalidateQueries({ queryKey: ["projects", workspaceId] });
      toast.success(`Proyecto "${project.name}" creado desde la plantilla`);
      onOpenChange(false);
      setSelected(null);
      navigate(`/space/${workspaceId}/project/${project.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (tpl) => {
    try {
      await api.delete(`/templates/${tpl.id}`);
      qc.invalidateQueries({ queryKey: ["templates", workspaceId] });
      toast.success("Plantilla eliminada");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setSelected(null); }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="templates-gallery-dialog">
        {selected ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <button onClick={() => setSelected(null)} data-testid="template-back-btn"
                  className="p-1 rounded hover:bg-secondary"><ArrowLeft className="h-4 w-4" /></button>
                {selected.icon} Usar "{selected.name}"
              </DialogTitle>
              <DialogDescription>Se creará un proyecto nuevo con {selected.task_count} tareas planificadas.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre del proyecto</Label>
                <Input data-testid="template-project-name-input" value={name}
                  onChange={(e) => setName(e.target.value)} maxLength={100} autoFocus />
              </div>
              <div className="space-y-2">
                <Label>Fecha de inicio (las tareas se planifican a partir de este día)</Label>
                <Input data-testid="template-start-date-input" type="date" value={startDate}
                  onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <Button className="w-full" onClick={create} disabled={creating} data-testid="template-confirm-create-btn">
                {creating ? "Creando…" : "Crear proyecto"}
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <LayoutTemplate className="h-5 w-5 text-primary" /> Galería de plantillas
              </DialogTitle>
              <DialogDescription>Crea proyectos listos en un clic. Guarda los tuyos desde el menú de cualquier proyecto.</DialogDescription>
            </DialogHeader>
            {isLoading ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Cargando plantillas…</p>
            ) : (
              <div className="space-y-5">
                <div className="space-y-2">
                  <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Plantillas del Espacio</h3>
                  {(data?.custom || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground rounded-lg border border-dashed p-4 text-center" data-testid="templates-empty-custom">
                      Aún no hay plantillas propias. Abre un proyecto → menú ⋮ → "Guardar como plantilla".
                    </p>
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-3">
                      {data.custom.map((t) => <TemplateCard key={t.id} tpl={t} onUse={pick} onDelete={remove} />)}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Plantillas nuxxum</h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {(data?.builtin || []).map((t) => <TemplateCard key={t.id} tpl={t} onUse={pick} onDelete={remove} />)}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
